import { createApi } from '@reduxjs/toolkit/query/react';
import { GetAllSchedulesFromTheDepartment, GetAllSchedulesByUpperAdmin } from "../../api/endpoints";
import { baseQueryWithReauth } from "../../api/baseQuery";

/**
 * Pick the schedule endpoint for a role list.
 *   - Department admins see only their own department.
 *   - Super / assistant / course admins see all schedules.
 *
 * Returns null when no role matches, so the query produces a clean error
 * result instead of issuing a request to an empty URL.
 */
function endpointForRoles(roles) {
    if (!Array.isArray(roles)) return null;
    if (roles.includes("org.admin.department")) {
        return GetAllSchedulesFromTheDepartment;
    }
    if (
        roles.includes("org.admin.super") ||
        roles.includes("org.admin.assistant") ||
        roles.includes("org.admin.course")
    ) {
        return GetAllSchedulesByUpperAdmin;
    }
    return null;
}

/**
 * Safely read the user's roles from localStorage. Returns [] if absent or
 * corrupt — previously a malformed value would throw uncaught from JSON.parse
 * inside the query function and crash the hook caller.
 */
function readRolesFromStorage() {
    try {
        const raw = localStorage.getItem("roles");
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export const scheduledCourseApi = createApi({
    reducerPath: 'scheduledCourseApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['ScheduledCourses'],
    endpoints: (builder) => ({
        // Optional `roles` arg is now part of the cache key. Existing callers
        // that pass nothing still work — they share one cache entry sourced
        // from localStorage. Callers that want role-specific cache invalidation
        // (e.g. on role change without a page reload) can pass roles explicitly.
        getScheduledCourses: builder.query({
            query: (roles) => {
                const effectiveRoles = roles ?? readRolesFromStorage();
                const endpoint = endpointForRoles(effectiveRoles);
                if (!endpoint) {
                    // No role match — issue a request that the backend will 404 on,
                    // so the consumer's `error` field is populated rather than the
                    // call silently succeeding with no data.
                    return { url: '__no_matching_role__', method: 'GET' };
                }
                return endpoint;
            },
            providesTags: ['ScheduledCourses'],
        }),
    }),
})

export const { useGetScheduledCoursesQuery } = scheduledCourseApi;


